import { Task, ProgressStats, TaskStatus } from '../types';

// API 基础路径 - 自动检测环境
const API_BASE = window.location.origin;

// 请求超时时间（毫秒）
const FETCH_TIMEOUT = 8000;

// 生成唯一ID
export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
};

// 带超时的通用 fetch 封装
async function apiFetch(url: string, options?: RequestInit): Promise<any> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      signal: controller.signal,
    });
    
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `请求失败: ${response.status}`);
    }
    
    if (response.status === 204) return null;
    return response.json();
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.error('API请求超时:', url);
      throw new Error('网络超时，请稍后重试');
    }
    console.error('API请求失败:', error);
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// 获取所有任务
export const getTasks = async (): Promise<Task[]> => {
  try {
    const data = await apiFetch(`${API_BASE}/api/tasks`);
    return data || [];
  } catch (error) {
    console.error('获取任务数据失败:', error);
    // 降级：尝试从 localStorage 读取
    try {
      const localData = localStorage.getItem('prolockey_video_tasks');
      return localData ? JSON.parse(localData) : [];
    } catch {
      return [];
    }
  }
};

// 新增任务
export const addTask = async (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>): Promise<Task> => {
  const newTask = await apiFetch(`${API_BASE}/api/tasks-add`, {
    method: 'POST',
    body: JSON.stringify(taskData),
  });
  return newTask;
};

// 更新任务
export const updateTask = async (id: string, updates: Partial<Task>): Promise<Task | null> => {
  const updatedTask = await apiFetch(`${API_BASE}/api/tasks-update`, {
    method: 'PUT',
    body: JSON.stringify({ id, ...updates }),
  });
  return updatedTask;
};

// 删除任务
export const deleteTask = async (id: string): Promise<boolean> => {
  const result = await apiFetch(`${API_BASE}/api/tasks-delete`, {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  });
  return result?.success || false;
};

// 批量更新状态
export const updateTaskStatus = (id: string, status: TaskStatus): Promise<Task | null> => {
  return updateTask(id, { status });
};

// 计算进度统计（前端计算）
export const getProgressStats = async (): Promise<ProgressStats> => {
  const tasks = await getTasks();
  const total = tasks.length;
  const pending = tasks.filter(t => t.status === 'pending').length;
  const filming = tasks.filter(t => t.status === 'filming').length;
  const editing = tasks.filter(t => t.status === 'editing').length;
  const completed = tasks.filter(t => t.status === 'completed').length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return { total, pending, filming, editing, completed, percentage };
};

// 初始化示例数据
export const initSampleData = async (): Promise<void> => {
  try {
    await apiFetch(`${API_BASE}/api/tasks-init`, {
      method: 'POST',
    });
  } catch (error) {
    // 静默失败 - 数据可能已经存在
    console.log('初始化数据跳过或已存在');
  }
};
